import { useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Stack,
 Button,
 Heading,
 HStack,
 Center,
 Container,
 useToast,
} from '@chakra-ui/react';
import { InputText, Select, YearPickerInput } from '../../../components/Input';
import Loader from '../../../components/Feedback/Loader';
import { QuestionnaireHeaderNew } from '../../../components/Form';
import { fetchData } from '../../../services/fetch';
import QuestionCardCustom from '../../../components/Card/Question-card-custom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { simpleRegisterSchema } from '../../../core/validations/simple_register';
import baseService from '../../../services/base';
import { SuccessModal } from '../../../components/Modals';
import { useNavigate } from 'react-router-dom';

export default function SimpleRegister() {
 const toast = useToast();
 const navigate = useNavigate();

 const [questionOptionsKey, setQuestionOptionsKey] = useState(0);
 const [selectedTemplate, setSelectedTemplate] = useState(null);
 const [isSecondSelectEnabled, setIsSecondSelectEnabled] = useState(false);
 const [selectedQuestions, setSelectedQuestions] = useState([]);
 const [tempSelectedQuestion, setTempSelectedQuestion] = useState(null);
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
 const [isReplicable, setIsReplicable] = useState();
 const [exercise, setExercise] = useState(null);

 const {
  handleSubmit,
  register,
  formState: { errors, isValid, isSubmitting },
 } = useForm({
  resolver: yupResolver(simpleRegisterSchema),
  mode: 'onChange',
 });

 const { data, isLoading } = useSWR(`/survey-template/all`, fetchData);

 const { data: surveyQuestions } = useSWR(
  selectedTemplate ? `/question-template/template/${selectedTemplate}` : null,
  fetchData
 );

 const templateOptions =
  data?.map((template) => ({
   label: template?.name,
   value: template?.id_survey_template,
  })) || [];

 const selectedQuestionIds = useMemo(() => {
  return new Set(selectedQuestions.map((q) => q.id_question_template));
 }, [selectedQuestions]);

 const questionOptions = useMemo(() => {
  if (!surveyQuestions?.length) {
   return [];
  }

  const options = [];
  if (selectedQuestionIds.size !== surveyQuestions.length) {
   options.push({ label: 'Todas as perguntas', value: 'all' });
  }

  const individualQuestions = surveyQuestions
   ?.filter((question) => !selectedQuestionIds.has(question.id_question_template))
   .map((question) => ({
    label: question?.questionTitleTemplate?.[0]?.description,
    value: question?.id_question_template,
   }));

  return [...options, ...individualQuestions];
 }, [surveyQuestions, selectedQuestionIds, questionOptionsKey]);

 const handleTemplateSelect = (value) => {
  setSelectedTemplate(value);
  setIsSecondSelectEnabled(true);
 };

 const handleQuestionSelect = (value) => {
  setTempSelectedQuestion(value);
 };

 const addSelectedQuestion = () => {
  if (tempSelectedQuestion === 'all') {
   const allUnselectedQuestions = surveyQuestions.filter(
    (question) => !selectedQuestionIds.has(question.id_question_template)
   );
   setSelectedQuestions((prev) => [...prev, ...allUnselectedQuestions]);
  } else {
   const selectedQuestion = surveyQuestions?.find(
    (question) => question?.id_question_template === +tempSelectedQuestion
   );
   if (
    selectedQuestion &&
    !selectedQuestionIds.has(selectedQuestion.id_question_template)
   ) {
    setSelectedQuestions((prev) => [...prev, selectedQuestion]);
   }
  }
  setQuestionOptionsKey((prevKey) => prevKey + 1);
  setTempSelectedQuestion(null); // Reset tempSelectedQuestion here
 };

 const removeQuestion = (questionId) => {
  setSelectedQuestions((prev) =>
   prev.filter((question) => question.id_question_template !== questionId)
  );
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const closeSuccessModal = () => {
  setIsSubmitSuccessful(false);
  navigate('/researches');
 };

 const onReplicableChange = (value) => {
  setIsReplicable(value);
 };

 const onSubmit = async (data) => {
  const { name } = data;

  const surveyQuestions = selectedQuestions?.map((q) => q?.id_question_template);

  const payload = {
   description: name,
   surveyQuestions: surveyQuestions,
   type: 'simple',
   ...(exercise ? { exercise: exercise.getFullYear() } : {}),
  };

  if (isReplicable === 'ADMIN' || isReplicable === 'ADMIN/RESPONDENT') {
   payload.is_replicable = true;
   payload.replica_type = isReplicable;
  }

  try {
   await baseService.post(`/survey/`, payload);
   setIsSubmitSuccessful(true);
  } catch (error) {
   showErrorToast('Não foi possível salvar o questionário');
  }
 };

 if (isLoading) {
  return <Loader />;
 }

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Cadastrar Questionário'
    isSubmitting={isSubmitting}
    isSubmitEnabled={isValid && selectedQuestions?.length}
    isSubmit={true}
    hasReplicableSelect
    onReplicableChange={onReplicableChange}
   >
    <HStack spacing={0}>
        <InputText
        {...register('name')}
        error={errors.name}
        placeholder='Digite o nome do questionário'
        autoComplete='off'
        w='340px'
        />
        <YearPickerInput
        value={exercise}
        onChange={(date) => setExercise(date)}
        />
    </HStack>
   </QuestionnaireHeaderNew>

   <Center my={['8px', '24px']} px={{ xl: '86px', '2xl': 'unset' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <HStack
      bg='white'
      justify='space-between'
      p='24px'
      borderRadius='12px 12px 0 0'
      borderBottom='2px solid #F5F5FB'
     >
      <Heading as='h3' fontSize='20px' fontWeight={600}>
       Templates
      </Heading>
     </HStack>

     <Stack bg='white' spacing='0px' p={6} borderRadius='0 0 12px 12px'>
      <HStack spacing={6} align='end' mb='24px'>
       <Select
        label='Template'
        placeholder='Selecione um template'
        options={templateOptions}
        onChange={(e) => handleTemplateSelect(e.target.value)}
       />
       <Select
        key={questionOptionsKey}
        label='Pergunta'
        placeholder='Selecione uma pergunta'
        options={questionOptions}
        isDisabled={!isSecondSelectEnabled || !surveyQuestions}
        onChange={(e) => handleQuestionSelect(e.target.value)}
       />
       <Button
        variant='primary'
        size='medium'
        w='137px'
        minW='137px'
        isDisabled={!tempSelectedQuestion || !tempSelectedQuestion.length}
        onClick={addSelectedQuestion}
       >
        Adicionar
       </Button>
      </HStack>
      <Stack spacing='16px'>
       {selectedQuestions?.map((question, i) => (
        <QuestionCardCustom
         key={question?.id_question_template}
         index={i}
         remove={() => removeQuestion(question?.id_question_template)}
         question={question}
        />
       ))}
      </Stack>
     </Stack>
    </Container>
   </Center>
   <SuccessModal
    isOpen={isSubmitSuccessful}
    onClose={closeSuccessModal}
    title='Questionário criado com sucesso!'
   />
  </form>
 );
}
